import { memo, useCallback } from "react";
import { StandardProps } from "@/types/types";
import classNames from "classnames/bind";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { dispatchLegacyAnalyticsEvent } from "@/utils/analytics";
import { extractAppliedFilters, removeAllPriceQueries } from "./utils";
import styles from "./AppliedFilters.module.scss";
import { useRouteParams } from "@/hooks/useRouteParams";
import { sanitiseQueryParams } from "@/utils/queryUtils";
import { getAnalyticsPageType } from "@/utils/setJLData";
import { LinkWithoutPrefetch } from "@/components/__shared__/LinkWithoutPrefetch/LinkWithoutPrefetch";
import { AppliedFilter } from "./AppliedFilter";
import useRouteLoading from "@/hooks/useRouteLoading";

const cx = classNames.bind(styles);

const CLEAR_FILTERS_THRESHOLD = 2;

interface AppliedFiltersProps {
  productListingData: StandardProps["productListingData"];
  configProps?: StandardProps["configProps"];
}

export const AppliedFilters = memo(
  ({ productListingData, configProps }: AppliedFiltersProps) => {
    const { baseFacetUrl, facets } = productListingData;
    const isHorizontal = Boolean(configProps?.isHorizontal);
    const { pageType, queryWithoutPage } = useRouteParams();
    const isRouteLoading = useRouteLoading();

    const { appliedFilters, combination, trackingIds } =
      extractAppliedFilters(facets);

    const dispatchFacetAnalytics = useCallback(
      (
        filterAction: string,
        facetName: string,
        label: string,
        trackingId: string,
      ) => {
        dispatchLegacyAnalyticsEvent(filterAction, {
          pagetype: getAnalyticsPageType(pageType, productListingData.results),
          // All applied filters *after* the filter has been removed i.e. the result
          filterCombination: !facetName
            ? ""
            : appliedFilters
                .filter(
                  (f) => !(f.facetName === facetName && f.label === label),
                )
                .map((f) => `${f.facetName}:${f.label}`.toLowerCase())
                .join("|"),
          filterCombinationNvals: !facetName
            ? ""
            : appliedFilters
                .filter(
                  (f) => !(f.facetName === facetName && f.label === label),
                )
                .map((f) => f.trackingId)
                .join("|"),
          appliedFilter: !facetName
            ? combination
            : `${facetName}:${label}`.toLowerCase(),
          appliedFilterNval: !facetName ? trackingIds : `${trackingId}`,
        });
      },
      [
        appliedFilters,
        pageType,
        productListingData.results,
        combination,
        trackingIds,
      ],
    );

    const handleFilterRemove = useCallback(
      (facetName: string, label: string, trackingId: string) => {
        dispatchFacetAnalytics(
          "plp.filterRemoved",
          facetName,
          label,
          trackingId,
        );
      },
      [dispatchFacetAnalytics],
    );

    const handleClearAllFilters = useCallback(() => {
      dispatchFacetAnalytics("plp.filterRemoved", "", "", "");
    }, [dispatchFacetAnalytics]);

    if (facets.length === 0) return null;
    if (appliedFilters.length === 0) return null;

    // Sort labels within each facetName, but preserve facetName order
    const facetNameOrder = appliedFilters.map((filter) => filter.facetName);
    const uniqueFacetNames = [...new Set(facetNameOrder)];

    appliedFilters.sort((facetA, facetB) => {
      const facetAIndex = uniqueFacetNames.indexOf(facetA.facetName);
      const facetBIndex = uniqueFacetNames.indexOf(facetB.facetName);

      // If they're the same facetName, sort by label alphabetically
      if (facetA.facetName === facetB.facetName) {
        return facetA.label
          .toLowerCase()
          .localeCompare(facetB.label.toLowerCase());
      }

      // Otherwise, sort by facetName order
      return facetAIndex - facetBIndex;
    });

    const rootUrl = pageType === "search" ? "/search" : baseFacetUrl;

    const clearAllFiltersQuery = sanitiseQueryParams(
      removeAllPriceQueries(queryWithoutPage),
    );
    const clearAllFiltersHref =
      clearAllFiltersQuery.length > 0
        ? `${rootUrl}?${clearAllFiltersQuery}`
        : rootUrl;

    // Common filter rendering logic
    const renderAppliedFilters = (keyPrefix: string = "") =>
      appliedFilters.map(({ facetName, label, trackingId, facetUrl }) => (
        <AppliedFilter
          key={keyPrefix ? `${keyPrefix}-${facetName}-${label}` : label}
          facetName={facetName}
          label={label}
          trackingId={trackingId}
          facetUrl={facetUrl}
          queryWithoutPage={queryWithoutPage}
          onFilterRemove={handleFilterRemove}
        />
      ));

    // Common clear all filters logic
    const renderClearAllFilters = () => {
      if (appliedFilters.length < CLEAR_FILTERS_THRESHOLD) return null;

      const itemClassName = isHorizontal
        ? cx("filter", "filter--clear-all")
        : undefined;

      return (
        <li className={itemClassName}>
          <UnderlinedButton
            onClick={handleClearAllFilters}
            tag={LinkWithoutPrefetch}
            href={clearAllFiltersHref}
            aria-label="Clear all filters"
            labelText="Clear all"
            className={cx(
              "clearAllButton",
              "clearAllButton--show-above-break-768",
            )}
          />
          <UnderlinedButton
            onClick={handleClearAllFilters}
            tag={LinkWithoutPrefetch}
            href={clearAllFiltersHref}
            aria-label="Clear all filters"
            labelText="Clear all filters"
            className={cx(
              "clearAllButton",
              "clearAllButton--hide-above-break-768",
            )}
          />
        </li>
      );
    };

    if (isHorizontal) {
      return (
        <div
          data-component="HorizontalAppliedFilters"
          className={cx("horizontalContainer", {
            "horizontalContainer--desktop-only": configProps?.desktopOnly,
            "u-loading": isRouteLoading,
          })}
        >
          <ul className={styles.filters} data-testid="applied-filters">
            {renderClearAllFilters()}
            {renderAppliedFilters("horizontal")}
          </ul>
        </div>
      );
    }

    return (
      <div
        data-component="AppliedFilters"
        className={cx("filters", {
          "filters--desktop-only": configProps?.desktopOnly,
          "filters--mobile-only": configProps?.mobileOnly,
        })}
      >
        <h3 className={styles.header}>Applied Filters</h3>
        <ul className={styles.filterList} data-testid="applied-filters">
          {renderAppliedFilters()}
          {renderClearAllFilters()}
        </ul>
      </div>
    );
  },
);

AppliedFilters.displayName = "AppliedFilters";
